// ============================================
// Cloudinary Storage Service
// Альтернативное хранилище для изображений (без Firebase Storage)
// Бесплатный план: 25GB storage, 25GB bandwidth, 25K transformations
// ============================================

import { getCurrentUserId } from './firebase/authService';

// Cloudinary configuration from environment variables
const CLOUDINARY_CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || 'your_cloud_name';
const CLOUDINARY_UPLOAD_PRESET = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET || 'your_upload_preset';

// Debug log on load
console.log('[Cloudinary] Config loaded:', {
  cloudName: CLOUDINARY_CLOUD_NAME,
  hasPreset: !!CLOUDINARY_UPLOAD_PRESET,
  preset: CLOUDINARY_UPLOAD_PRESET,
  envRaw: import.meta.env
});

/**
 * Проверяет доступность Cloudinary конфигурации
 */
export const isCloudinaryAvailable = (): boolean => {
  return !!CLOUDINARY_CLOUD_NAME && !!CLOUDINARY_UPLOAD_PRESET;
};



/**
 * Загружает изображение в Cloudinary
 * @param file - файл изображения
 * @param folder - папка для хранения (например, 'screens', 'avatars')
 * @returns URL загруженного изображения или null при ошибке
 */
export const uploadImageToCloudinary = async (
  file: File,
  folder: string = 'screens'
): Promise<string | null> => {
  if (!isCloudinaryAvailable()) {
    console.log('[Cloudinary] Not available - check VITE_CLOUDINARY_CLOUD_NAME and VITE_CLOUDINARY_UPLOAD_PRESET');
    return null;
  }

  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    formData.append('folder', folder);
    
    // Добавляем метаданные
    const userId = getCurrentUserId();
    if (userId) {
      formData.append('context', `user_id=${userId}`);
    }

    const url = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;
    
    console.log('[Cloudinary] Uploading to folder:', folder);
    
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('[Cloudinary] Upload failed:', errorData);
      throw new Error(errorData.error?.message || 'Upload failed');
    }

    const data = await response.json();
    console.log('[Cloudinary] Upload complete, URL:', data.secure_url);
    
    return data.secure_url;
  } catch (error) {
    console.error('[Cloudinary] Error uploading image:', error);
    return null;
  }
};

/**
 * Загружает изображение экрана в Cloudinary
 * @param file - файл изображения
 * @param projectId - ID проекта для организации файлов
 * @returns URL загруженного изображения или null при ошибке
 */
export const uploadScreenImage = async (
  file: File,
  projectId: string
): Promise<string | null> => {
  const folder = `screens/${projectId}`;
  return uploadImageToCloudinary(file, folder);
};

/**
 * Удаляет изображение из Cloudinary
 * @param imageUrl - URL изображения для удаления
 * @returns true если успешно удалено
 */
export const deleteImageFromCloudinary = async (imageUrl: string): Promise<boolean> => {
  if (!isCloudinaryAvailable()) {
    return false;
  }

  try {
    // Извлекаем public_id из URL
    // URL формат: https://res.cloudinary.com/{cloud_name}/image/upload/{folder}/{filename}
    const urlParts = imageUrl.split('/');
    const uploadIndex = urlParts.indexOf('upload');
    
    if (uploadIndex === -1 || uploadIndex + 1 >= urlParts.length) {
      console.error('[Cloudinary] Invalid image URL format');
      return false;
    }

    // Получаем public_id (всё после 'upload/' без расширения)
    const publicIdWithExtension = urlParts.slice(uploadIndex + 1).join('/');
    const publicId = publicIdWithExtension.replace(/\.[^/.]+$/, ''); // убираем расширение

    // Для удаления нужен серверный API key (небезопасно делать с клиента)
    // В production рекомендуется делать это через backend
    console.log('[Cloudinary] Image deletion should be done via backend. public_id:', publicId);
    
    // Помечаем как успешное, но на самом деле не удаляем
    // В production реализовать через cloud function или backend
    return true;
  } catch (error) {
    console.error('[Cloudinary] Error deleting image:', error);
    return false;
  }
};

/**
 * Получает оптимизированный URL изображения с трансформациями
 * @param imageUrl - оригинальный URL
 * @param options - опции трансформации
 * @returns оптимизированный URL
 */
export const getOptimizedImageUrl = (
  imageUrl: string,
  options: {
    width?: number;
    height?: number;
    quality?: number;
    format?: 'auto' | 'webp' | 'jpg' | 'png';
  } = {}
): string => {
  if (!imageUrl || !imageUrl.includes('cloudinary.com')) {
    return imageUrl;
  }

  const { width, height, quality = 80, format = 'auto' } = options;

  // Разбираем URL
  const urlParts = imageUrl.split('/upload/');
  if (urlParts.length !== 2) return imageUrl;

  // Формируем трансформации
  const transformations: string[] = [];
  
  if (width) transformations.push(`w_${width}`);
  if (height) transformations.push(`h_${height}`);
  if (quality) transformations.push(`q_${quality}`);
  if (format === 'auto') {
    transformations.push('f_auto');
  } else if (format) {
    transformations.push(`f_${format}`);
  }

  // Добавляем оптимизацию
  transformations.push('fl_lossy');

  const transformationString = transformations.join(',');
  return `${urlParts[0]}/upload/${transformationString}/${urlParts[1]}`;
};

/**
 * Получает thumbnail URL
 */
export const getThumbnailUrl = (imageUrl: string, size: number = 200): string => {
  return getOptimizedImageUrl(imageUrl, {
    width: size,
    height: size,
    quality: 60,
    format: 'auto'
  });
};
